import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { CUSTOMER_SEARCH_FIELDS } from '@app/shared';
import type { CustomerSearchField } from '@app/shared';
import { useAuth } from '../features/auth/AuthProvider';
import { useFeatureReleased } from '../release';
import { toQueryString } from '../lib/api';

/** Field lengths of the reference's Advanced Search form. */
const MAX_LENGTH: Partial<Record<CustomerSearchField, number>> = {
  phone: 10,
  accountNo: 12,
  enrollmentId: 12,
  firstName: 20,
  lastName: 20,
  alternateContact: 10,
};

type Criteria = Record<CustomerSearchField, string>;
const EMPTY = Object.fromEntries(CUSTOMER_SEARCH_FIELDS.map(([key]) => [key, ''])) as Criteria;

/** The reference's "Advanced Search" dialog: ten fields, Cancel / Reset / Search. */
function AdvancedSearchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [criteria, setCriteria] = useState<Criteria>(EMPTY);
  const [problem, setProblem] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const filled = Object.fromEntries(
      Object.entries(criteria)
        .map(([key, value]) => [key, value.trim()])
        .filter(([, value]) => value),
    );
    if (Object.keys(filled).length === 0) {
      setProblem(true);
      return;
    }
    onClose();
    navigate(`/customers${toQueryString(filled)}`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={submit} noValidate>
        <DialogTitle>Advanced Search</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            {CUSTOMER_SEARCH_FIELDS.map(([key, label], i) => (
              <TextField
                key={key}
                label={label}
                autoFocus={i === 0}
                value={criteria[key]}
                onChange={(e) => {
                  setCriteria((c) => ({ ...c, [key]: e.target.value }));
                  setProblem(false);
                }}
                error={problem}
                helperText={problem && i === 0 ? 'Enter at least one search value' : undefined}
                slotProps={{ htmlInput: { maxLength: MAX_LENGTH[key] } }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => {
              setCriteria(EMPTY);
              setProblem(false);
            }}
          >
            Reset
          </Button>
          <Button type="submit" variant="contained">
            Search
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/**
 * Header "Search Customer" box with the search menu next to it. The box looks a customer up
 * by one value (customer ID, telephone number, enrollment ID or SIM); the menu, as the
 * reference's, offers Inventory Search (the box's value looked up in inventory), Payment Search,
 * Recent Searches and Advanced Search. The first two have release switches.
 */
export function CustomerSearch() {
  const { can } = useAuth();
  const featureOn = useFeatureReleased();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const [advanced, setAdvanced] = useState(false);

  if (!can('PC400') || !can('PC56')) return null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = text.trim();
    if (!q) return;
    setText('');
    navigate(`/customers${toQueryString({ q })}`);
  };

  return (
    <>
      <Box
        component="form"
        role="search"
        onSubmit={submit}
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: 305,
          maxWidth: '40vw',
          height: 34,
          bgcolor: '#fff',
          borderRadius: 1,
          pl: 1.5,
        }}
      >
        <InputBase
          placeholder="Search Customer"
          value={text}
          onChange={(e) => setText(e.target.value)}
          inputProps={{ 'aria-label': 'Search Customer', maxLength: 40 }}
          sx={{ flexGrow: 1, fontSize: 14 }}
        />
        <Tooltip title="Search">
          <IconButton type="submit" size="small" color="primary" aria-label="Search customer">
            <ArrowForwardIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Button
        color="inherit"
        endIcon={<ArrowDropDownIcon />}
        onClick={(e) => setMenu(e.currentTarget)}
        aria-haspopup="menu"
        aria-label="Search options"
        sx={{ fontSize: 15 }}
      >
        Search
      </Button>
      <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
        {can('PC345') && featureOn('header-inventory-search') ? (
          <MenuItem
            onClick={() => {
              setMenu(null);
              navigate(`/inventory/search${toQueryString({ q: text.trim() })}`);
            }}
          >
            Inventory Search
          </MenuItem>
        ) : null}
        {can('PC339') && featureOn('header-payment-search') ? (
          <MenuItem
            onClick={() => {
              setMenu(null);
              navigate('/customers/payment-search');
            }}
          >
            Payment Search
          </MenuItem>
        ) : null}
        {can('PC295') ? (
          <MenuItem
            onClick={() => {
              setMenu(null);
              navigate('/customers/recent');
            }}
          >
            Recent Searches
          </MenuItem>
        ) : null}
        <MenuItem
          onClick={() => {
            setMenu(null);
            setAdvanced(true);
          }}
        >
          Advanced Search
        </MenuItem>
      </Menu>
      {/* Mounted only while open, so every opening starts with empty fields. */}
      {advanced ? <AdvancedSearchDialog open onClose={() => setAdvanced(false)} /> : null}
    </>
  );
}
